import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Separator, Stack, Typography } from '@locus-taxy/disco-ui';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { EncoreFormRenderer } from '@locus-taxy/encore-form';
import type { FormRendererRef } from '@locus-taxy/encore-form';
import { getTestId } from '@locus-taxy/encore-utils';
import { TestIdAction } from '../../utils/testIdUtils';
import type { ProfileViewMode, ViewField } from './profileTypes';
import { ProfileViewRenderer } from './ProfileViewRenderer';
import { buildLocaleSettingsSchema } from './profileFormSchemas';
import {
  buildTimezoneOptionsWithOffset,
  hasSelectableOptions,
} from './localeSettingsSectionUtils';

interface LocaleSettingsSectionProps {
  readonly mode: ProfileViewMode;
  readonly clientId: string;
  readonly formRef: React.RefObject<FormRendererRef | null>;
  readonly onChange?: (data: Record<string, unknown>) => void;
  /** What the page displays and previews: the stored value, else the client default, else the browser's. */
  readonly timezone: string;
  readonly timezoneOptions: string[];
  readonly locale: string;
  readonly localeOptions: string[];
  /**
   * What the record holds, null when unset. The form starts from these, never from the display
   * fallback: it reports its whole state on any change, so a seeded fallback would be saved.
   */
  readonly storedTimezone: string | null;
  readonly storedLocale: string | null;
  readonly viewFields: ViewField[];
}

function formatDateTimePreview(timezone: string, locale: string): string {
  try {
    const now = new Date();
    const formatted = new Intl.DateTimeFormat(locale, {
      dateStyle: 'short',
      timeStyle: 'short',
      timeZone: timezone,
    }).format(now);
    return `${formatted} (${locale})`;
  } catch {
    return '—';
  }
}

function DateTimePreview({
  timezone,
  locale,
}: Readonly<{
  timezone: string;
  locale: string;
}>) {
  const { t } = useTranslation();
  const preview = useMemo(
    () => formatDateTimePreview(timezone, locale),
    [timezone, locale]
  );

  return (
    <Stack className="gap-1 pt-2">
      <Typography variant="body2" fontWeight="medium" color="muted">
        {t(
          'dashboard.userDetails.dateTimeFormatPreview',
          'Date/Time Format Preview'
        )}
      </Typography>
      <Typography variant="body1">{preview}</Typography>
      <Typography variant="caption" color="muted">
        {t(
          'dashboard.userDetails.dateTimeFormatHelp',
          'This is how dates and times will appear across the dashboard.'
        )}
      </Typography>
    </Stack>
  );
}

export function LocaleSettingsSection({
  mode,
  clientId,
  formRef,
  onChange,
  timezone,
  timezoneOptions,
  locale,
  localeOptions,
  storedTimezone,
  storedLocale,
  viewFields,
}: LocaleSettingsSectionProps) {
  const { t } = useTranslation();

  const [liveTimezone, setLiveTimezone] = useState(timezone);
  const [liveLocale, setLiveLocale] = useState(locale);

  useEffect(() => {
    setLiveTimezone(timezone);
    setLiveLocale(locale);
  }, [timezone, locale, mode]);

  const timezoneSelectOptions = useMemo(
    () =>
      buildTimezoneOptionsWithOffset(timezoneOptions, storedTimezone).map(
        opt => ({
          label: opt.label,
          value: opt.value,
        })
      ),
    [timezoneOptions, storedTimezone]
  );

  /* The form renders only when the list is non-empty (hasSelectableOptions below). */
  const localeSelectOptions = useMemo(
    () => localeOptions.map(opt => ({ label: opt, value: opt })),
    [localeOptions]
  );

  const schema = useMemo(
    () =>
      buildLocaleSettingsSchema(
        {
          timezoneOptions: timezoneSelectOptions,
          localeOptions: localeSelectOptions,
        },
        t
      ),
    [timezoneSelectOptions, localeSelectOptions, t]
  );

  const formInitialData = useMemo(
    () => ({
      preferences: { timezone: storedTimezone, localeIdentifier: storedLocale },
    }),
    [storedTimezone, storedLocale]
  );

  const handleFormChange = useCallback(
    (data: Record<string, unknown>) => {
      const prefs = data.preferences as Record<string, unknown> | undefined;
      if (prefs) {
        if (typeof prefs.timezone === 'string') {
          setLiveTimezone(prefs.timezone);
        }
        if (typeof prefs.localeIdentifier === 'string') {
          setLiveLocale(prefs.localeIdentifier);
        }
      }
      onChange?.(data);
    },
    [onChange]
  );

  const previewTimezone = mode === 'edit' ? liveTimezone : timezone;
  const previewLocale = mode === 'edit' ? liveLocale : locale;

  /* Edit mode renders the form only when there is something to choose from: an empty option list
     cannot be saved from, so the reason is said instead. */
  const editContent = hasSelectableOptions(timezoneOptions, localeOptions) ? (
    <>
      <EncoreFormRenderer
        ref={formRef}
        schema={schema as any}
        clientId={clientId}
        initialData={formInitialData}
        onChange={handleFormChange}
        routeContext={{ page: 'profile' }}
      />
      <DateTimePreview timezone={previewTimezone} locale={previewLocale} />
    </>
  ) : (
    <Typography
      variant="body2"
      color="destructive"
      data-testid={getTestId({
        action: TestIdAction.VIEW,
        object: 'locale-options-error',
        page: 'account',
      })}
    >
      {t(
        'dashboard.userDetails.localeOptionsUnavailable',
        'Timezone and language options could not be loaded, so these settings cannot be changed right now. Please refresh, or contact your administrator if this continues.'
      )}
    </Typography>
  );
  return (
    <div
      className="rounded-md border border-border bg-card"
      data-testid={getTestId({
        action: TestIdAction.VIEW,
        object: 'locale-settings-section',
        page: 'account',
      })}
    >
      <Stack gap={3} className="p-8">
        <Stack className="gap-1">
          <Typography variant="h6" fontWeight="semibold">
            {t('dashboard.userDetails.localeSettings', 'Locale Settings')}
          </Typography>
          <Typography variant="body2" color="muted">
            {t(
              'dashboard.userDetails.localeDescription',
              'These settings will reflect changes across the dashboard.'
            )}
          </Typography>
        </Stack>

        {mode === 'view' ? (
          <Stack gap={3}>
            <ProfileViewRenderer fields={viewFields} columns={2} />
            <Separator />
            <DateTimePreview timezone={timezone} locale={locale} />
          </Stack>
        ) : (
          editContent
        )}
      </Stack>
    </div>
  );
}
